import { useState } from "react"
import { cn } from "@/lib/cn"
import { toneCss } from "../format"
import type { ActionButtonBlock, ActionPayload } from "../types"

type Props = {
  block: ActionButtonBlock
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}

// Single button that fires a partner-side action via Valet's portal
// proxy. Clicking opens an optional confirmation modal, then the
// `onAction` callback. Loading state during the request; sonner toast
// surfaces on error from the parent hook.
export function ActionButtonBlockView({ block, onAction }: Props) {
  const [busy, setBusy] = useState(false)
  const css = toneCss(block.tone)

  async function click() {
    if (busy) return
    if (block.confirm && !window.confirm(block.confirm)) return
    setBusy(true)
    try {
      const action: ActionPayload = {
        label:     block.label,
        method:    block.method,
        path:      block.path,
        body:      block.body,
        confirm:   block.confirm,
        tone:      block.tone,
        icon:      block.icon,
        action_id: block.action_id,
        refresh:   block.refresh,
      }
      await onAction(action)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={cn(
        "valet-uidoc-actionbutton inline-flex min-h-[44px] items-center justify-center gap-2 rounded-control border px-4 py-2 text-sm font-semibold transition",
        css.text,
        css.bg,
        css.border,
        "hover:brightness-110 disabled:cursor-wait disabled:opacity-60",
      )}
    >
      {busy ? "Working…" : block.label}
    </button>
  )
}
